/* eslint-disable @nx/enforce-module-boundaries */
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, forkJoin, map, of, switchMap, take } from 'rxjs';
import { AgencyOfficerDetails, AgencyOfficerDetailsPermissions, OfficerPermissions } from '../models/officer-details.models';
import { AgencyOfficerDetailsResponse } from '../models/officer-details-response.models';
import * as AgencyOfficerDetailsActions from './agency-officer-store.actions';
import { AgencyOfficerDetailsService } from '../services/agency-officer-details.service';
import { OfficerUploadImageService } from '../services/officer-upload-image.service';
import { NotificationService } from '@shared/feature/notification-handling';
import { AgencyOfficerDetailsFacade } from './agency-officer-store.facade';
import { ActionType, UpdateOfficerDetailsPermissionsActionResult } from '../models/officer-details-action-result.models';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalService } from '@shared/feature/modal';
import { ListOfficerFacade } from '@shared/feature/list-officer';
import { TranslationFromJsonService } from '@shared/feature/translate';
import { CacheService, CacheUsersInitiator } from '@shared/utils/cache';

@Injectable()
export class AgencyOfficerDetailsEffects {
  fetchAgencyOfficerDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgencyOfficerDetailsActions.fetchAgencyOfficerDetails),
      switchMap((action) =>
        forkJoin({
          officer: this.service.fetchAgencyOfficerDetails(action.uid),
          permissions: this.service.fetchAgencyOfficerPermissions(action.uid),
        })
      ),
      map(({ officer, permissions }) =>
        AgencyOfficerDetailsActions.fetchAgencyOfficerDetailsSuccess({
          agencyOfficerDetailsPermissions: new AgencyOfficerDetailsPermissions(
            this.transformAgencyOfficerDetailsResponse(officer),
            new OfficerPermissions(permissions.CanDownloadFiles, permissions.CanUploaderAccess, permissions.CanWebAccess, permissions.IsDisabled)
          ),
        })
      )
    )
  );

  updateAgencyOfficerDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgencyOfficerDetailsActions.updateAgencyOfficerDetails),
      exhaustMap((action) => {
        return this.facade.getAgencyOfficerDetails().pipe(
          take(1),
          switchMap((x) => {
            return this.service.updateAgencyOfficerDetails(x.agencyOfficerDetails.uid, action.agencyOfficerDetails).pipe(
              map((response: AgencyOfficerDetailsResponse) => {
                const officer = this.transformAgencyOfficerDetailsResponse(response);
                this.notificationService.addSuccess(this.translationFromJsonService.instant('changes-saved'));
                this.modalService.closeAllModals();
                this.listOfficerFacade.listOfficer();

                this.cacheService.usersDataModified(CacheUsersInitiator.UserUpdated);

                return AgencyOfficerDetailsActions.updateAgencyOfficerDetailsSuccess({
                  agencyOfficerDetails: officer,
                });
              }),
              catchError((error: HttpErrorResponse) =>
                of(
                  AgencyOfficerDetailsActions.updateAgencyOfficerActionResult({
                    actionResult: new UpdateOfficerDetailsPermissionsActionResult(ActionType.UpdateAgencyOfficerDetails, error instanceof HttpErrorResponse ? error : '', false),
                  })
                )
              )
            );
          })
        );
      })
    )
  );

  updateAgencyOfficerPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgencyOfficerDetailsActions.updateAgencyOfficerPermissions),
      exhaustMap((action) => {
        return this.facade.getAgencyOfficerDetails().pipe(
          take(1),
          switchMap((x) => {
            return this.service.updateAgencyOfficerPermissions(x.agencyOfficerDetails.uid, action.agencyOfficerPermissions).pipe(
              map(() => {
                this.notificationService.addSuccess(this.translationFromJsonService.instant('changes-saved'));

                return AgencyOfficerDetailsActions.updateAgencyOfficerPermissionsSuccess({
                  agencyOfficerPermissions: new OfficerPermissions(
                    action.agencyOfficerPermissions.CanDownloadFiles,
                    action.agencyOfficerPermissions.CanUploaderAccess,
                    action.agencyOfficerPermissions.CanWebAccess,
                    action.agencyOfficerPermissions.IsDisabled
                  ),
                });
              }),
              catchError((error: HttpErrorResponse) =>
                of(
                  AgencyOfficerDetailsActions.updateAgencyOfficerActionResult({
                    actionResult: new UpdateOfficerDetailsPermissionsActionResult(ActionType.UpdateAgencyOfficerDetails, error instanceof HttpErrorResponse ? error : '', false),
                  })
                )
              )
            );
          })
        );
      })
    )
  );

  updateAgencyPhoto$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AgencyOfficerDetailsActions.updateAgencyOfficerPhoto),
      exhaustMap((action) => {
        return this.uploadImageService.uploadImageApi(action.photo).pipe(
          map((newUrl) => {
            this.notificationService.addSuccess(this.translationFromJsonService.instant('user-photo-successfully-changed'));
            this.modalService.closeAllModals();

            return AgencyOfficerDetailsActions.updateAgencyOfficerPhotoSuccess({ newUrl: newUrl });
          }),
          catchError(() => {
            this.notificationService.addError(this.translationFromJsonService.instant('something-went-wrong'));

            return of(AgencyOfficerDetailsActions.updateAgencyOfficerPhotoFailure());
          })
        );
      })
    );
  });

  constructor(
    private service: AgencyOfficerDetailsService,
    private actions$: Actions,
    private notificationService: NotificationService,
    private uploadImageService: OfficerUploadImageService,
    private facade: AgencyOfficerDetailsFacade,
    private modalService: ModalService,
    private listOfficerFacade: ListOfficerFacade,
    private translationFromJsonService: TranslationFromJsonService,
    private cacheService: CacheService
  ) {}

  private transformAgencyOfficerDetailsResponse(response: AgencyOfficerDetailsResponse): AgencyOfficerDetails {
    return new AgencyOfficerDetails(
      response.TimeZone,
      response.CreatedOn,
      response.Phone,
      response.Role,
      response.ShareType,
      response.RespondTo,
      response.Children,
      response.FirstName,
      response.LastName,
      response.EmployeeID,
      response.EmailAddress,
      response.PictureFullPath,
      response.Timezone,
      response.UID
    );
  }
}
